const STORAGE_KEY = 'service-booking.settling-payments';

/**
 * How long a paid booking is treated as settling. SBS normally confirms within
 * a second and took 46 s at worst; it refuses to cancel a paid booking on its
 * own, so the flag only has to cover the usual delay. Kept short because a cart
 * that 404s for another reason also sets it, and that booking has no Leave.
 */
export const SETTLING_PAYMENT_TTL_MS = 2 * 60_000;

type SettlingPayments = Record<string, number>;

// Mirror of the stored map, so a blocked or full sessionStorage still works for
// the life of this app instance.
let memory: SettlingPayments = {};

const parseStored = (raw: string | null): SettlingPayments => {
  const stored: unknown = raw ? JSON.parse(raw) : null;

  return stored && typeof stored === 'object' ? (stored as SettlingPayments) : {};
};

// Later expiry wins per booking: another same-origin frame may have stored a
// newer entry, and a failed write leaves an older one behind in storage.
const read = (): SettlingPayments => {
  const merged = { ...memory };
  try {
    Object.entries(parseStored(sessionStorage.getItem(STORAGE_KEY))).forEach(([bookingUuid, expiresAt]) => {
      merged[bookingUuid] = Math.max(merged[bookingUuid] ?? 0, expiresAt);
    });
  } catch {
    // Storage unreadable: the in-memory copy is all there is.
  }

  return merged;
};

const write = (payments: SettlingPayments): void => {
  memory = payments;
  try {
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(payments));
  } catch {
    // Running on the in-memory copy alone is acceptable.
  }
};

/**
 * Remember that this tab paid for a booking (SMB-1785). The charge succeeds
 * before SBS hears about it: the booking stays PENDING_PAYMENT until the
 * payment event lands (normally under a second, but it has taken 46 s). Until
 * then the page must not treat the booking as an abandoned checkout, i.e.
 * reopen checkout, offer "Book now" again, or let Leave cancel it.
 *
 * Kept in sessionStorage so a reload in the same tab still knows.
 */
export const markPaymentSettling = (bookingUuid: string): void => {
  const now = Date.now();
  const live = Object.fromEntries(Object.entries(read()).filter(([, expiresAt]) => expiresAt > now));
  write({ ...live, [bookingUuid]: now + SETTLING_PAYMENT_TTL_MS });
};

/** Whether this tab paid for the booking within {@link SETTLING_PAYMENT_TTL_MS}. */
export const isPaymentSettling = (bookingUuid: string | null | undefined): boolean => {
  if (!bookingUuid) return false;
  const expiresAt = read()[bookingUuid];

  return expiresAt !== undefined && expiresAt > Date.now();
};

/** Test-only: forget every settling payment. */
export const resetSettlingPayments = (): void => {
  memory = {};
  try {
    sessionStorage.removeItem(STORAGE_KEY);
  } catch {
    // Nothing stored to clear.
  }
};
